import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useCartaObjectStorage from "hooks/useCartaObjectStorage";

interface ProjectErrorsContextValue {
  /** The current Post Submit Target error count for this project. */
  count: number;
  /** Re-fetches the count immediately. */
  refresh: () => Promise<void>;
  /** Announces that a form was just saved, (re)starting the post-save polling window. */
  notifySaved: () => void;
}

const ProjectErrorsContext = createContext<ProjectErrorsContextValue | undefined>(undefined);

// A just-saved form's Post Submit Targets may take a little while to be hit and
// to fail, so once a save happens, keep re-checking for a while rather than
// only on mount.
const POLL_INTERVAL_MS = 30_000;
const POLL_DURATION_MS = 5 * 60_000;

/**
 * Tracks a single project's Post Submit Target error count in one shared place.
 * Anything that displays it (the header badge, and potentially others) reads
 * from this same context instead of independently fetching/polling, so every
 * display for a given project always agrees.
 */
export const ProjectErrorsProvider: FunctionComponent<PropsWithChildren<{ projectId: string }>> = ({
  projectId,
  children,
}) => {
  const [count, setCount] = useState<number>(0);
  const cartaObjectStorage = useCartaObjectStorage();
  const pollHandle = useRef<{ interval: ReturnType<typeof setInterval>; timeout: ReturnType<typeof setTimeout> } | null>(null);

  const refresh = useCallback(async () => {
    if (!cartaObjectStorage || !projectId) return;
    try {
      const response = await cartaObjectStorage.getPstErrors({ projectId });
      setCount(response.count);
    } catch {
      setCount(0);
    }
  }, [cartaObjectStorage, projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const stopPolling = useCallback(() => {
    if (!pollHandle.current) return;
    clearInterval(pollHandle.current.interval);
    clearTimeout(pollHandle.current.timeout);
    pollHandle.current = null;
  }, []);

  const notifySaved = useCallback(() => {
    // A save restarts the polling window, rather than stacking on top of one
    // already in progress.
    stopPolling();
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    const timeout = setTimeout(stopPolling, POLL_DURATION_MS);
    pollHandle.current = { interval, timeout };
  }, [refresh, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const value = useMemo(() => ({ count, refresh, notifySaved }), [count, refresh, notifySaved]);

  return <ProjectErrorsContext.Provider value={value}>{children}</ProjectErrorsContext.Provider>;
};

export function useProjectErrors(): ProjectErrorsContextValue {
  const context = useContext(ProjectErrorsContext);
  if (!context) {
    throw new Error("useProjectErrors must be used within a ProjectErrorsProvider");
  }
  return context;
}
